import { AsyncButton, PauseCover, loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { IRequestStatus } from "functions/api/interface";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { ButtonGroup, Col, Row } from "react-bootstrap";
import { eApplicationPermission } from "../../functions/api/enums";
import { NewsItem, loadNews } from "../../functions/api/news/loadNews";
import { saveNews } from "../../functions/api/news/saveNews";
import { NewsItemsDisplay } from "./NewsItemsDisplay";

/**
 * News management section. Shows current news items and allows users with
 * permission to add, edit and save news entries to the backend.
 */
export const News = (): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [changed, setChanged] = useState<boolean>(false);
  const [loadRequest, setLoadRequest] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const [saveRequest, setSaveRequest] = useState<IRequestStatus<NewsItem[]>>({
    requesting: false,
    error: false,
  });

  const allowNewsEdit =
    appSettings.applicationPermissions.findIndex(
      (p) => p === eApplicationPermission.AllowNewsEdit,
    ) > -1 || appSettings.isAdmin;

  const refreshNews = useEffectEvent(async () => {
    // Get news
    const request = await loadWrapper(
      loadNews,
      "loadNews",
      { ...loadRequest, requesting: false },
      setLoadRequest,
      appSettings.backEndUrl,
    );

    if (request?.success) {
      setNewsItems(request.data);
    } else if (request?.errorText) {
      setNewsItems([]);
      console.warn(`Load news failed: ${request?.errorText}`);
    }
  });
  useEffect(() => {
    /* istanbul ignore else */
    if (newsItems.length === 0 && !loadRequest.error && !loadRequest.requesting) {
      refreshNews();
    }
  }, [loadRequest.error, loadRequest.requesting, newsItems.length]);

  /**
   * Add a new blank news item
   */
  const addNews = () => {
    const nN = [...newsItems];
    const n: NewsItem = {
      timestamp: Date.now(),
      displayName:
        appSettings.displayName ??
        // istanbul ignore next
        "Test user",
      title: "",
      text: "",
    };
    nN.push(n);
    setNewsItems(nN);
    setChanged(true);
  };

  /**
   * Update a news item
   *
   * @param newNews - the updated news item
   * @param i - index to update
   */
  const updateNews = (newNews: NewsItem, i: number) => {
    const nN = [
      ...(newsItems ??
        // istanbul ignore next
        []),
    ];
    nN.splice(i, 1, newNews);
    setNewsItems(nN);
    setChanged(true);
  };

  /**
   * Remove a news item
   *
   * @param i - index to remove
   */
  const removeNews = (i: number) => {
    const nN = [
      ...(newsItems ??
        // istanbul ignore next
        []),
    ];
    nN.splice(i, 1);
    setNewsItems(nN);
    setChanged(true);
  };

  /**
   * Save the current news items
   */
  const saveCurrentNews = async () => {
    const request = await loadWrapper(
      saveNews,
      newsItems ??
        // istanbul ignore next
        [],
      { ...saveRequest, requesting: false },
      setSaveRequest,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      setNewsItems(request.data);
      setChanged(false);
    } else if (request?.errorText) {
      console.warn(`Save failed for news: ${request?.errorText} `);
    }
  };

  return (
    <Row className="pb-3 position-relative">
      {loadRequest.requesting && <PauseCover text="Loading news..." />}
      <Col>
        <h5>News</h5>
        {newsItems &&
          newsItems.map((n, i) => (
            <NewsItemsDisplay
              id={`news-item-${i}`}
              key={i}
              item={n}
              setItem={allowNewsEdit ? (ret) => updateNews(ret, i) : undefined}
              removeItem={allowNewsEdit ? () => removeNews(i) : undefined}
            />
          ))}
        {newsItems && newsItems.length === 0 && (
          <NewsItemsDisplay
            id="news-display-0"
            item={{
              title: "",
              displayName: "",
              timestamp: 0,
              text: "The news is, there is no news",
            }}
          />
        )}
        {allowNewsEdit && (
          <ButtonGroup>
            <AsyncButton
              id="news-add"
              aria-label="Add news"
              style={{ marginLeft: "130px" }}
              onClick={() => addNews()}
              variant="primary"
              size="sm"
            >
              Add news
            </AsyncButton>
            <AsyncButton
              id="news-save"
              aria-label="Save news"
              aria-disabled={!changed}
              disabled={!changed}
              variant="primary"
              onClick={() => saveCurrentNews()}
              style={{ minWidth: "100px", minHeight: "38px", position: "relative" }}
              requestStatus={saveRequest}
              pauseCoverText="Saving..."
            >
              Save news
            </AsyncButton>
          </ButtonGroup>
        )}
      </Col>
    </Row>
  );
};

News.displayName = "News";
